import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { Image as ImageIcon, Search, Loader2 } from "lucide-react";
import { getAllImages } from "../../services/imageService";
import { cn } from "../../lib/utils";

// ---------------------------------------------------------------------------
// Image Picker Dialog (reusable for editors)
// ---------------------------------------------------------------------------
export default function ImagePicker({ value, onChange, label = "Image" }) {
  const [open, setOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) {
      setLoading(true);
      getAllImages()
        .then((data) => setImages(data))
        .catch(() => toast.error("Gagal memuat gambar"))
        .finally(() => setLoading(false));
    }
  }, [open]);

  const filtered = search
    ? images.filter(
        (img) =>
          img.id?.toLowerCase().includes(search.toLowerCase())
      )
    : images;

  const selectedSrc = value?.content || value?.url || null;

  return (
    <div className="space-y-2">
      <label className="text-sm text-[#a6a196]">{label}</label>

      <div className="flex items-center gap-3">
        {value ? (
          <div className="flex items-center gap-2 flex-1">
            <div className="w-12 h-12 rounded-md overflow-hidden border border-[#d4af37]/20 bg-[#0a0908] shrink-0">
              {selectedSrc ? (
                <img
                  src={selectedSrc}
                  alt="selected"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="w-4 h-4 text-[#a6a196]" />
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-[#fdfbf7] truncate">
                {value.name || value.id?.slice(0, 12) || "Gambar dipilih"}
              </p>
              <p className="text-[11px] text-[#a6a196]">
                {value.type || "gambar"}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex-1">
            <p className="text-sm text-[#a6a196]/60 italic">Belum ada gambar dipilih</p>
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-[#d4af37]/30 text-[#d4af37] shrink-0"
            >
              {value ? "Ganti" : "Pilih Gambar"}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#15120f] border-[#d4af37]/20 text-[#fdfbf7] max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-[#fdfbf7] flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-[#d4af37]" />
                Pilih Gambar
              </DialogTitle>
            </DialogHeader>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a6a196]" />
              <Input
                placeholder="Cari gambar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50"
              />
            </div>

            {/* Grid */}
            <ScrollArea className="h-80">
              {loading ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="w-6 h-6 text-[#d4af37] animate-spin" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-[#a6a196]">
                  <ImageIcon className="w-8 h-8 mb-2 opacity-30" />
                  <p className="text-sm">Tidak ada gambar ditemukan</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 p-1">
                  {filtered.map((img) => {
                    const src = img.content || img.url;
                    return (
                      <button
                        key={img.id}
                        type="button"
                        onClick={() => {
                          onChange(img);
                          setOpen(false);
                        }}
                        className={cn(
                          "group relative aspect-square rounded-lg overflow-hidden border-2 transition-all duration-200 bg-[#0a0908]",
                          value?.id === img.id
                            ? "border-[#d4af37] ring-1 ring-[#d4af37]/50"
                            : "border-[#d4af37]/10 hover:border-[#d4af37]/40"
                        )}
                      >
                        {src ? (
                          <img
                            src={src}
                            alt={img.id}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-6 h-6 text-[#a6a196]/30" />
                          </div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="text-[10px] text-white truncate">
                            {img.id?.slice(0, 12)}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
